<template>
  <div
    :class="{
      'note-with-icon': true,
      'note-with-icon--gray': props.color === 'gray',
      'note-with-icon--green': props.color === 'green',
      'note-with-icon--red': props.color === 'red',
      'note-with-icon--blue': props.color === 'blue',

      'note-with-icon--small': props.size === 'small',
      'note-with-icon--tiny': props.size === 'tiny',

      'note-with-icon--transparent-bg': props.isTransparentBg,
    }"
    ref="domRefRoot"
  >
    <div
      v-if="slots.icon || props.icon"
      :class="{
        'note-with-icon__icon': true,
        'note-with-icon__icon--gray': props.color === 'gray',
        'note-with-icon__icon--green': props.color === 'green',
        'note-with-icon__icon--red': props.color === 'red',
        'note-with-icon__icon--blue': props.color === 'blue',

        'note-with-icon__icon--small': props.size === 'small',
        'note-with-icon__icon--tiny': props.size === 'tiny',
      }"
    >
      <slot name="icon">
        <IconEmbedded :name="props.icon" :size="props.iconSize || { tiny: 16, small: 18, normal: 26 }[props.size]" />
      </slot>
    </div>

    <div class="note-with-icon__text">
      <slot name="text" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, useSlots } from "vue";

// Types
import { IconName } from "@contracts/IconName";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const props = withDefaults(
  defineProps<{
    color?: "gray" | "yellow" | "green" | "red" | "blue";
    size?: "tiny" | "small" | "normal";
    icon?: IconName;
    iconSize?: number;
    isTransparentBg?: boolean;
  }>(),
  {
    color: "yellow",
    size: "normal",
    icon: "info-simple_4",
    iconSize: null,
    isTransparentBg: false,
  }
);

const slots = useSlots();

// Expose the root node =======================================================
const domRefRoot = ref<HTMLElement | null>(null);

defineExpose({
  domRefRoot,
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Note with icon =============================================================
.note-with-icon {
  max-width: 100%;
  box-sizing: border-box;
  padding: 14px 23px;
  border-radius: 6px;
  border: 1px solid #dfd6ac;
  border-left: 6px solid #8b7a28;
  box-shadow: 0 6px 16px rgba(65, 56, 6, 0.08);
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 0;
  color: #413806;
  font: 400 14px/18px sans-serif;
  background: #f0e7b8;

  :deep(.note-with-icon__icon .embedded-icon svg) {
    fill: #413806;
  }

  :deep(a:hover) {
    text-decoration: none;
  }

  // prettier-ignore
  :deep(h1),:deep(h2),:deep(h3),:deep(h4),:deep(h5),:deep(h6),:deep(.global-h1),:deep(.global-h2),:deep(.global-h3),:deep(.global-h4),:deep(.global-h5),:deep(.global-h6) { color: #413806; }

  // color ====================================================================
  &--gray {
    color: #5b5b5b;
    background: #f7f7f7;
    border: 1px solid #dddddd;
    border-left: 6px solid #8a8a8a;
    box-shadow: 0 6px 16px rgba(30, 30, 30, 0.08);
    :deep(.note-with-icon__icon .embedded-icon svg) {
      fill: #5b5b5b;
    }
    // prettier-ignore
    :deep(h1),:deep(h2),:deep(h3),:deep(h4),:deep(h5),:deep(h6),:deep(.global-h1),:deep(.global-h2),:deep(.global-h3),:deep(.global-h4),:deep(.global-h5),:deep(.global-h6) { color: #5b5b5b; }

    :deep(a:not(.srp-button)) {
      color: #5b5b5b !important;
      text-decoration: underline;
      cursor: pointer;
    }
  }
  &--green {
    color: #1f3320;
    background: #f8fcf5;
    border: 1px solid #d8e7cf;
    border-left: 6px solid #4a7a2d;
    box-shadow: 0 6px 16px rgba(21, 53, 24, 0.08);
    :deep(.note-with-icon__icon .embedded-icon svg) {
      fill: #264726;
    }
    // prettier-ignore
    :deep(h1),:deep(h2),:deep(h3),:deep(h4),:deep(h5),:deep(h6),:deep(.global-h1),:deep(.global-h2),:deep(.global-h3),:deep(.global-h4),:deep(.global-h5),:deep(.global-h6) { color: #1f3320; }

    :deep(a:not(.srp-button)) {
      color: #1f3320 !important;
      text-decoration: underline;
      cursor: pointer;
    }
  }
  &--red {
    color: #8b4a3f;
    background: #fff3f1;
    border: 1px solid #f1d2cc;
    border-left: 6px solid #bb6a5c;
    box-shadow: 0 6px 16px rgba(96, 36, 24, 0.08);
    :deep(.note-with-icon__icon .embedded-icon svg) {
      fill: #8b4a3f;
    }
    // prettier-ignore
    :deep(h1),:deep(h2),:deep(h3),:deep(h4),:deep(h5),:deep(h6),:deep(.global-h1),:deep(.global-h2),:deep(.global-h3),:deep(.global-h4),:deep(.global-h5),:deep(.global-h6) { color: #8b4a3f; }

    :deep(a:not(.srp-button)) {
      color: #8b4a3f !important;
      text-decoration: underline;
      cursor: pointer;
    }
  }
  &--blue {
    color: #234949;
    background: #f3fbfb;
    border: 1px solid #cfe8e8;
    border-left: 6px solid #4e8f8f;
    box-shadow: 0 6px 16px rgba(29, 73, 73, 0.08);
    :deep(.note-with-icon__icon .embedded-icon svg) {
      fill: #234949;
    }
    // prettier-ignore
    :deep(h1),:deep(h2),:deep(h3),:deep(h4),:deep(h5),:deep(h6),:deep(.global-h1),:deep(.global-h2),:deep(.global-h3),:deep(.global-h4),:deep(.global-h5),:deep(.global-h6) { color: #234949; }

    :deep(a:not(.srp-button)) {
      color: #234949 !important;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  // size =====================================================================
  &--tiny {
    padding: 9px 15px 9px 11px;
  }
  &--small {
    padding: 13px 15px;
  }

  &--transparent-bg {
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
  }

  &__icon {
    width: 40px;
    min-width: 40px;
    height: 40px;
    margin-right: 16px;
    border-radius: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #d9ce95;

    :deep(.embedded-icon svg) {
      width: 23px;
      height: 23px;
    }

    &--gray {
      background: #e4e4e4;
    }
    &--green {
      background: #d6ebc9;
    }
    &--red {
      background: #f3d1cb;
    }
    &--blue {
      background: #d3ecec;
    }

    &--tiny {
      width: 26px;
      min-width: 26px;
      height: 26px;
      margin-right: 8px;
    }
    &--small {
      width: 33px;
      min-width: 33px;
      height: 33px;
      margin-right: 8px;
    }
  }

  &__text {
    flex-grow: 1;
    padding-bottom: 3px;
    align-self: center;
    word-break: break-word;
    text-wrap: balance;

    :deep(*) {
      text-wrap: balance;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .note-with-icon {
    //padding: 16px 23px;
    padding: 16px 17px;

    &--tiny {
      padding: 9px 11px;
    }
    &--small {
      padding: 13px 15px;
    }

    &__icon {
      margin-right: 13px;
    }
  }
}
</style>
